import { useState } from "react";
import { LAB_SETTINGS_DEFAULTS, ReportSignerLabel, RoleLabel, type Branch, type ReportSigner, type Role, type TenantSettingsSetPayload, type TenantSettingsSetResult, type User } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import { useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Button, Card, Chip, Definition, Empty, Field, Input, Loading, PageHead, Select, Sheet, Switch, Tabs } from "../components/ui.js";
import { InstructionImagesCard } from "../components/InstructionImages.js";
import { LetterheadCard } from "../components/Letterhead.js";
import { PlatformLoginCard } from "../components/PlatformLogin.js";

type Tab = "users" | "branches" | "lab" | "letterhead" | "instructions" | "activity" | "support";

/** Administración: users and roles, the branches, the laboratory's preferences, the result letterhead, the patient instruction images, and the activity log read back from the audit chain. */
export function SettingsPage() {
  const { me, isOperator } = useSession();
  const [tab, setTab] = useState<Tab>("users");
  const users = useQuery(() => get("users"), []);
  const activity = useQuery(() => get("activity", { query: { limit: 100 } }), [], { enabled: tab === "activity" });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  return (
    <>
      <PageHead title="Configuración" lede={me?.tenant.name} />
      <Tabs value={tab} onChange={setTab} items={[{ value: "users", label: "Usuarios", count: users.data?.items.length }, { value: "branches", label: "Sucursales", count: me?.branches.length }, { value: "lab", label: "Preferencias" }, { value: "letterhead", label: "Membrete" }, { value: "instructions", label: "Indicaciones" }, { value: "activity", label: "Bitácora" }, ...(isOperator ? [{ value: "support" as const, label: "Soporte" }] : [])]} />

      {tab === "users" && (
        <Card title="Usuarios y permisos" actions={<Button variant="primary" icon="userPlus" onClick={() => setCreating(true)}>Nuevo usuario</Button>} pad={false}>
          {users.loading && !users.data ? (
            <Loading />
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Rol</th>
                    <th>Permisos</th>
                    <th>Cédula</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.data?.items.map((u) => (
                    <tr key={u.id} style={u.is_active ? undefined : { opacity: 0.5 }}>
                      <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                      <td className="muted">{u.email}</td>
                      <td>
                        <Chip tone={u.role === "administrador" ? "violet" : u.role === "quimico" ? "blue" : "neutral"}>{RoleLabel[u.role]}</Chip>
                      </td>
                      <td>
                        <span className="row wrap" style={{ gap: 6 }}>
                          {u.can_validate && <Chip tone="green">Valida</Chip>}
                          {u.can_close_cash && <Chip tone="amber">Cierra caja</Chip>}
                          {!u.is_active && <Chip>Inactivo</Chip>}
                        </span>
                      </td>
                      <td className="mono muted">{u.cedula ?? "—"}</td>
                      <td className="right">
                        <Button size="sm" variant="ghost" icon="pen" onClick={() => setEditing(u)}>
                          Editar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "branches" && (
        <div className="grid-2">
          {me?.branches.map((b) => (
            <Card key={b.id} title={b.name} actions={<Chip tone="green">{b.short_code}</Chip>}>
              <div className="stack" style={{ gap: 12 }}>
                <Definition k="Dirección" v={b.address ?? "—"} />
                <Definition k="Teléfono" v={b.phone ?? "—"} />
                <Definition k="Responsable sanitario" v={b.responsible_name ?? "—"} />
                <Definition k="Cédula" v={b.responsible_cedula ?? "—"} />
                <BranchSignerControl branch={b} users={users.data?.items ?? []} />
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "lab" && <LabPreferences />}

      {tab === "letterhead" && <LetterheadCard />}

      {tab === "instructions" && <InstructionImagesCard />}

      {tab === "support" && isOperator && <PlatformLoginCard />}

      {tab === "activity" && (
        <Card title="Bitácora de actividad" pad={false}>
          {activity.loading && !activity.data ? (
            <Loading />
          ) : !activity.data?.items.length ? (
            <Empty icon="history" title="Sin eventos" />
          ) : (
            <div className="table-scroll">
              <table className="table compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cuándo</th>
                    <th>Quién</th>
                    <th>Acción</th>
                    <th>Sobre</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.data.items.map((e) => (
                    <tr key={e.seq}>
                      <td className="tnum faint">{e.seq}</td>
                      <td className="small">{fmtDateTime(e.received_at)}</td>
                      <td>
                        {e.actor_name ?? <span className="faint">sistema</span>}
                        {e.actor_kind === "operator" ? <span className="chip violet actor-kind">Soporte Probeta Lab</span> : null}
                      </td>
                      <td className="mono small">{e.action}</td>
                      <td className="small muted">
                        {e.entity_type}
                        {e.entity_id ? <span className="faint"> · {e.entity_id.slice(0, 8)}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <UserSheet open={creating} onClose={() => setCreating(false)} onSaved={() => void users.reload()} />
      <UserSheet open={editing !== null} user={editing} onClose={() => setEditing(null)} onSaved={() => void users.reload()} />
    </>
  );
}

/**
 * The laboratory's interface settings (`tenant.settings.set`, Administrador; audited in the
 * chain). Support sees the value but does not change it: how the lab's screens look is its call.
 */
function LabPreferences() {
  const { me, isOperator, refresh } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const settings = me?.lab_settings;

  async function change(patch: TenantSettingsSetPayload, done: string) {
    if (busy) return;
    setBusy(true);
    try {
      await run<"tenant.settings.set", TenantSettingsSetResult>("tenant.settings.set", patch);
      await refresh();
      toast.success(done);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const rows: { key: "compact_sidebar" | "samples_collected_at_registration" | "capture_low_red" | "report_text_flag" | "report_validator_line"; label: string; hint: string; on: string; off: string }[] = [
    {
      key: "compact_sidebar",
      label: "Menú compacto (solo iconos)",
      hint: "El menú muestra solo los iconos; el nombre aparece al pasar el mouse.",
      on: "Menú compacto activado.",
      off: "Menú compacto desactivado.",
    },
    {
      key: "samples_collected_at_registration",
      label: "Las muestras se toman al registrar",
      hint: "Los tubos nacen como tomados; marca como pendiente solo el que falte.",
      on: "Los tubos se registran como tomados.",
      off: "Los tubos se registran como pendientes.",
    },
    {
      key: "capture_low_red",
      label: "Captura: bajos en rojo, altos en naranja",
      hint: "En la captura de resultados, el valor bajo se marca en rojo y el alto en naranja. Apagado, ambos se marcan en naranja. Siempre dice Alto o Bajo.",
      on: "Captura: bajos en rojo, altos en naranja.",
      off: "Captura: altos y bajos en naranja.",
    },
    {
      key: "report_text_flag",
      label: "Reporte: marcar con * los resultados de texto distintos de la referencia",
      hint: "Por ejemplo, POSITIVO cuando lo esperado es NEGATIVO. Apagado, el reporte no imprime ese * ni la línea «*: Resultado distinto del valor de referencia.» de la leyenda. *A y *B se imprimen siempre. Aplica a los reportes que se generen desde ahora.",
      on: "El reporte marca con * los resultados de texto distintos.",
      off: "El reporte ya no marca con * los resultados de texto.",
    },
    {
      key: "report_validator_line",
      label: "Reporte: indicar quién validó cada estudio",
      hint: "Debajo de cada estudio se imprime «Validado por: nombre, Céd. Prof. número.» aunque firme la responsable sanitaria. Aplica a los reportes que se generen desde ahora.",
      on: "El reporte indica quién validó cada estudio.",
      off: "El reporte ya no indica quién validó cada estudio.",
    },
  ];

  const validity = settings?.quote_validity_days ?? LAB_SETTINGS_DEFAULTS.quote_validity_days;

  return (
    <Card title="Preferencias del laboratorio">
      <div className="stack-lg">
        {rows.map((r) => {
          const value = settings?.[r.key] ?? LAB_SETTINGS_DEFAULTS[r.key];
          return (
            <div key={r.key} className="stack" style={{ gap: 6 }}>
              {isOperator ? (
                <Definition k={r.label} v={value ? "Activado" : "Desactivado"} />
              ) : (
                <Switch checked={value} onChange={(v) => void change({ [r.key]: v }, v ? r.on : r.off)}>
                  {r.label}
                </Switch>
              )}
              <p className="small muted" style={{ margin: 0 }}>
                {r.hint}
              </p>
            </div>
          );
        })}
        <QuoteValidity key={validity} days={validity} readOnly={isOperator} busy={busy} onSave={(n) => change({ quote_validity_days: n }, `Las cotizaciones valen ${n} ${n === 1 ? "día" : "días"}.`)} />
      </div>
    </Card>
  );
}

/** Days a new quote stays valid (lab setting `quote_validity_days`); saved with its own button. */
function QuoteValidity({ days, readOnly, busy, onSave }: { days: number; readOnly: boolean; busy: boolean; onSave: (n: number) => Promise<void> }) {
  const [text, setText] = useState(String(days));
  const n = Number(text);
  const valid = text.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= 365;
  return (
    <div className="stack" style={{ gap: 6 }}>
      {readOnly ? (
        <Definition k="Vigencia de las cotizaciones" v={`${days} días`} />
      ) : (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <Field label="Vigencia de las cotizaciones (días)" error={text !== "" && !valid ? "Escribe un número de días entre 1 y 365." : null}>
            <Input type="number" inputMode="numeric" min={1} max={365} value={text} onChange={(e) => setText(e.target.value)} style={{ maxWidth: 140 }} />
          </Field>
          <Button variant="secondary" disabled={!valid || n === days || busy} onClick={() => void onSave(n)}>
            Guardar
          </Button>
        </div>
      )}
      <p className="small muted" style={{ margin: 0 }}>
        Las cotizaciones nuevas se imprimen y se envían con esta vigencia. Las ya emitidas conservan la suya.
      </p>
    </div>
  );
}

function UserSheet({ open, user, onClose, onSaved }: { open: boolean; user?: User | null; onClose: () => void; onSaved: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const [v, setV] = useState({ full_name: "", email: "", role: "recepcion" as Role, can_validate: false, can_close_cash: false, cedula: "", default_branch_id: "", password: "", is_active: true });
  const [busy, setBusy] = useState(false);
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && user && seeded !== user.id) {
    setV({ full_name: user.full_name, email: user.email, role: user.role, can_validate: user.can_validate, can_close_cash: user.can_close_cash, cedula: user.cedula ?? "", default_branch_id: user.default_branch_id ?? "", password: "", is_active: user.is_active });
    setSeeded(user.id);
  }
  if (open && !user && seeded !== "new") {
    setV({ full_name: "", email: "", role: "recepcion", can_validate: false, can_close_cash: false, cedula: "", default_branch_id: me?.branches[0]?.id ?? "", password: "", is_active: true });
    setSeeded("new");
  }
  const valid = v.full_name.trim().length >= 3 && (user ? true : v.email.includes("@") && v.password.length >= 10);

  async function save() {
    setBusy(true);
    try {
      if (user) {
        await run("user.update", { user_id: user.id, full_name: v.full_name.trim(), role: v.role, can_validate: v.can_validate, can_close_cash: v.can_close_cash, cedula: v.cedula.trim() || null, default_branch_id: v.default_branch_id || null, is_active: v.is_active });
        toast.success("Usuario actualizado.");
      } else {
        await run("user.create", { email: v.email.trim(), full_name: v.full_name.trim(), role: v.role, can_validate: v.can_validate, can_close_cash: v.can_close_cash, cedula: v.cedula.trim() || null, default_branch_id: v.default_branch_id || null, password: v.password });
        toast.success("Usuario creado.");
      }
      setSeeded(null);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={() => {
        setSeeded(null);
        onClose();
      }}
      title={user ? `Editar a ${user.full_name}` : "Nuevo usuario"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!valid} onClick={() => void save()}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="grid-2">
        <Field label="Nombre completo">
          <Input value={v.full_name} onChange={(e) => setV({ ...v, full_name: e.target.value })} autoFocus />
        </Field>
        <Field label="Correo" hint={user ? "El correo no se cambia." : undefined}>
          <Input type="email" value={v.email} disabled={!!user} onChange={(e) => setV({ ...v, email: e.target.value })} />
        </Field>
        <Field label="Rol">
          <Select value={v.role} onChange={(e) => setV({ ...v, role: e.target.value as Role })}>
            <option value="recepcion">Recepción</option>
            <option value="quimico">Químico</option>
            <option value="administrador">Administrador</option>
          </Select>
        </Field>
        <Field label="Sucursal habitual">
          <Select value={v.default_branch_id} onChange={(e) => setV({ ...v, default_branch_id: e.target.value })}>
            <option value="">Sin sucursal fija</option>
            {me?.branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Cédula profesional" optional hint="Se imprime junto a la firma al validar.">
          <Input value={v.cedula} onChange={(e) => setV({ ...v, cedula: e.target.value })} inputMode="numeric" />
        </Field>
        {!user && (
          <Field label="Contraseña inicial" hint="Mínimo 10 caracteres.">
            <Input type="password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} autoComplete="new-password" />
          </Field>
        )}
      </div>
      <div className="row wrap" style={{ gap: 24 }}>
        <Switch checked={v.can_validate} onChange={(c) => setV({ ...v, can_validate: c })}>
          Puede validar resultados
        </Switch>
        <Switch checked={v.can_close_cash} onChange={(c) => setV({ ...v, can_close_cash: c })}>
          Puede cerrar caja
        </Switch>
        {user && (
          <Switch checked={v.is_active} onChange={(c) => setV({ ...v, is_active: c })}>
            Activo
          </Switch>
        )}
      </div>
    </Sheet>
  );
}

/**
 * Who signs this branch's reports (migration 0013, NOM-007 5.1.8): each validator, or the
 * responsable sanitaria on every report. Only active users who validate can be chosen. Saving
 * refreshes the session so the branch list shows the new setting.
 */
export function BranchSignerControl({ branch, users }: { branch: Branch; users: User[] }) {
  const { refresh } = useSession();
  const toast = useToast();
  const [mode, setMode] = useState<ReportSigner>(branch.report_signer);
  const [userId, setUserId] = useState<string>(branch.responsible_user_id ?? "");
  const [busy, setBusy] = useState(false);
  const signers = users.filter((u) => u.can_validate && (u.is_active || u.id === branch.responsible_user_id));
  const dirty = mode !== branch.report_signer || userId !== (branch.responsible_user_id ?? "");
  const valid = mode === "validator" || userId !== "";

  async function save() {
    setBusy(true);
    try {
      await run("branch.report_signer.set", { branch_id: branch.id, report_signer: mode, responsible_user_id: userId || null });
      await refresh();
      toast.success("Firma de los reportes actualizada.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 12 }} data-testid={`branch-signer-${branch.short_code}`}>
      <Field label="Firma de los reportes" hint={mode === "responsible" ? "Todos los reportes de esta sucursal salen con su firma, nombre y cédula, aunque los valide otra persona. Quién validó cada estudio queda registrado." : "Cada reporte sale con la firma de quien validó sus estudios."}>
        <Select value={mode} onChange={(e) => setMode(e.target.value as ReportSigner)} aria-label="Quién firma los reportes">
          <option value="validator">{ReportSignerLabel.validator}</option>
          <option value="responsible">{ReportSignerLabel.responsible}</option>
        </Select>
      </Field>
      {mode === "responsible" && (
        <Field label="Responsable sanitaria que firma" error={signers.length === 0 ? "No hay usuarios activos que validen resultados." : null}>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Responsable sanitaria que firma">
            <option value="">Elige a la persona</option>
            {signers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
                {u.cedula ? ` · Céd. ${u.cedula}` : ""}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {dirty && (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Button variant="primary" icon="check" busy={busy} disabled={!valid} onClick={() => void save()}>
            Guardar firma
          </Button>
        </div>
      )}
    </div>
  );
}
